import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import {
    collection, onSnapshot, query, addDoc, updateDoc, doc,
    deleteDoc, serverTimestamp, arrayUnion
} from 'firebase/firestore';
import { Phone, Plus, Star, X, Mail, Trash2, Edit, LogIn } from 'lucide-react';
import './PlayerMarket.css';
import { turkeyCities as TURKEY_CITIES } from '../data/turkeyCities';

const POSITIONS = ['Kaleci', 'Defans', 'Ortasaha', 'Forvet'];
const EMPTY = { name: '', surname: '', position: 'Forvet', city: '', district: '', phone: '', email: '', consent: false };

const PlayerMarket = () => {
    // --- SADECE GOOGLE GİRİŞİ ---
    const { currentUser, loginWithGoogle } = useAuth();
    const userId = currentUser?.uid || null;

    const [players, setPlayers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [authLoading, setAuthLoading] = useState(false);
    const [form, setForm] = useState(EMPTY);

    const [searchCity, setSearchCity] = useState("");
    const [searchDistrict, setSearchDistrict] = useState("");
    const [searchPosition, setSearchPosition] = useState("Tüm Mevkiler");

    useEffect(() => {
        const unsub = onSnapshot(query(collection(db, "players")), (snap) => {
            setPlayers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, []);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const myExistingPost = userId ? players.find((p) => p.userId === userId) : null;

    const handleGoogleLogin = async () => {
        setAuthLoading(true);
        try {
            await loginWithGoogle();
        } catch (err) {
            alert("Google ile giriş başarısız oldu: " + (err.message || err));
        } finally {
            setAuthLoading(false);
        }
    };

    const openAddModal = () => {
        const parts = (currentUser?.displayName || '').trim().split(' ');
        setEditingId(null);
        setForm({
            ...EMPTY,
            name: parts[0] || '',
            surname: parts.slice(1).join(' ') || '',
            email: currentUser?.email || '',
        });
        setShowModal(true);
    };

    const openEditModal = (player) => {
        setEditingId(player.id);
        setForm({ ...EMPTY, ...player });
        setShowModal(true);
    };

    const handlePrimaryClick = () => {
        if (!userId) return handleGoogleLogin();
        if (myExistingPost) openEditModal(myExistingPost);
        else openAddModal();
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!userId) return;

        if (!form.name || form.name.trim().length < 2) return alert("Lütfen geçerli bir isim giriniz.");
        if (!form.surname || form.surname.trim().length < 2) return alert("Lütfen geçerli bir soyisim giriniz.");
        if (!form.city) return alert("Lütfen şehir seçiniz.");
        if (!form.phone && !form.email) return alert("En az bir iletişim bilgisi (telefon veya e-posta) giriniz.");
        if (form.phone) {
            const clean = form.phone.replace(/\s/g, '');
            if (!/^05[0-9]{9}$/.test(clean)) {
                return alert("Telefon numarasını 05XX XXX XX XX biçiminde giriniz.");
            }
        }
        if (!form.consent) return alert("Devam etmek için bilgilendirme metnini kabul etmelisiniz.");

        const payload = {
            name: form.name.trim(),
            surname: form.surname.trim(),
            position: form.position,
            city: form.city,
            district: form.district,
            phone: form.phone.trim(),
            email: form.email.trim(),
            consent: true,
        };

        try {
            if (editingId) {
                await updateDoc(doc(db, "players", editingId), {
                    ...payload,
                    updatedAt: serverTimestamp(),
                });
                alert("Profilin güncellendi. ✅");
            } else {
                await addDoc(collection(db, "players"), {
                    ...payload,
                    userId,
                    ownerEmail: currentUser?.email || '',
                    consentAt: serverTimestamp(),
                    rating: 0,
                    votes: 0,
                    votedBy: [],
                    createdAt: serverTimestamp(),
                });
                alert("Profilin Saha Yıldızları'na eklendi. ⚽");
            }
            setShowModal(false);
        } catch (err) {
            alert("Hata: " + err.message);
        }
    };

    const handleDelete = async (playerId) => {
        if (!window.confirm("İlanını silmek istediğine emin misin?")) return;
        try {
            await deleteDoc(doc(db, "players", playerId));
        } catch (err) {
            alert("Silme hatası: " + err.message);
        }
    };

    const handleRate = async (id, newRating) => {
        if (!userId) return alert("Oy vermek için Google ile giriş yapmalısınız.");
        const player = players.find((p) => p.id === id);
        if (!player) return;
        if (player.userId === userId) return alert("Kendinize oy veremezsiniz.");
        if ((player.votedBy || []).includes(userId)) return alert("Bu oyuncuya zaten oy verdiniz.");

        const currentVotes = player.votes || 0;
        const total = (player.rating || 0) * currentVotes;
        const updatedVotes = currentVotes + 1;
        const updatedRating = Number(((total + newRating) / updatedVotes).toFixed(1));

        try {
            await updateDoc(doc(db, "players", id), {
                rating: updatedRating,
                votes: updatedVotes,
                votedBy: arrayUnion(userId),
            });
        } catch (err) {
            console.error("Oylama hatası:", err);
        }
    };

    const filtered = players.filter((p) => {
        const cityMatch = !searchCity || (p.city || "").toLowerCase().includes(searchCity.toLowerCase());
        const distMatch = !searchDistrict || (p.district || "").toLowerCase().includes(searchDistrict.toLowerCase());
        const posMatch = searchPosition === "Tüm Mevkiler" || p.position === searchPosition;
        return cityMatch && distMatch && posMatch;
    });

    const sorted = [...filtered].sort((a, b) => {
        if (userId && a.userId === userId) return -1;
        if (userId && b.userId === userId) return 1;
        return (b.rating || 0) - (a.rating || 0);
    });

    return (
        <div className="market-wrapper">
            <nav className="market-navbar">
                <div className="nav-left"></div>
                <div className="nav-right">
                    {userId ? (
                        <button
                            className={`add-star-btn ${myExistingPost ? 'edit-mode-btn' : ''}`}
                            onClick={handlePrimaryClick}
                        >
                            {myExistingPost ? (
                                <><Edit size={16} /> Profilimi Düzenle</>
                            ) : (
                                <><Plus size={16} /> Kendini Ekle</>
                            )}
                        </button>
                    ) : (
                        <button className="add-star-btn" onClick={handleGoogleLogin} disabled={authLoading}>
                            <LogIn size={16} /> {authLoading ? "Giriş yapılıyor..." : "Google ile Giriş"}
                        </button>
                    )}
                </div>
            </nav>

            <div className="market-content">
                <div className="page-header">
                    <h1>Saha Yıldızları</h1>
                    <p>Takımına yıldız oyuncu bul veya yeteneklerini sergile.</p>
                </div>

                {!userId && (
                    <div className="market-login-hint">
                        <p>
                            Kendini listeye eklemek için <b>Google ile giriş yap</b>.
                            Ad, soyad, mevki ve iletişim bilgisi yeterli.
                        </p>
                        <button
                            className="save-modal-btn market-login-btn"
                            onClick={handleGoogleLogin}
                            disabled={authLoading}
                        >
                            {authLoading ? "Giriş yapılıyor..." : "Google ile Giriş Yap"}
                        </button>
                    </div>
                )}

                <div className="search-bar-container">
                    <select
                        className="search-select"
                        value={searchCity}
                        onChange={(e) => { setSearchCity(e.target.value); setSearchDistrict(""); }}
                    >
                        <option value="">Şehir Seçin</option>
                        {Object.keys(TURKEY_CITIES).map((city) => (
                            <option key={city} value={city}>{city}</option>
                        ))}
                    </select>

                    <select
                        className="search-select"
                        value={searchDistrict}
                        onChange={(e) => setSearchDistrict(e.target.value)}
                        disabled={!searchCity}
                    >
                        <option value="">İlçe Seçin</option>
                        {searchCity && TURKEY_CITIES[searchCity].map((dist) => (
                            <option key={dist} value={dist}>{dist}</option>
                        ))}
                    </select>

                    <select
                        className="search-select"
                        value={searchPosition}
                        onChange={(e) => setSearchPosition(e.target.value)}
                    >
                        <option>Tüm Mevkiler</option>
                        {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>

                <div className="cards-grid">
                    {sorted.map((player) => (
                        <div
                            key={player.id}
                            className={`pro-card ${userId && player.userId === userId ? 'my-card-highlight' : ''}`}
                        >
                            {userId && player.userId === userId && (
                                <>
                                    <div className="edit-icon" onClick={() => openEditModal(player)} title="Düzenle">
                                        <Edit size={16} />
                                    </div>
                                    <div className="delete-icon" onClick={() => handleDelete(player.id)} title="Sil">
                                        <Trash2 size={16} />
                                    </div>
                                    <div className="owner-badge">BENİM İLANIM</div>
                                </>
                            )}

                            <div className="card-header-visual">
                                <div className="avatar-circle">
                                    {(player.name || '?').charAt(0).toUpperCase()}
                                    <div className={`position-badge ${(player.position || '').toLowerCase().replace(/\s/g, '')}`}>
                                        {player.position}
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">
                                <h3>{player.name} {player.surname || ''}</h3>
                                <p className="sub-text">
                                    {player.age ? `${player.age} Yaşında • ` : ''}
                                    {player.city} {player.district ? `/ ${player.district}` : ''}
                                </p>

                                <div className="contact-area">
                                    {player.phone ? (
                                        <div className="contact-item">
                                            <Phone size={14} color="#2ecc71" /> <span>{player.phone}</span>
                                        </div>
                                    ) : null}
                                    {player.email ? (
                                        <div className="contact-item">
                                            <Mail size={14} color="#3498db" /> <span>{player.email}</span>
                                        </div>
                                    ) : null}
                                    {!player.phone && !player.email && (
                                        <div className="contact-item dimmed">
                                            <Phone size={14} /> <span>Gizli</span>
                                        </div>
                                    )}
                                </div>

                                <div className="rating-section">
                                    <div className="stars-interactive">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <Star
                                                key={star}
                                                size={18}
                                                fill={star <= Math.round(player.rating || 0) ? "#f1c40f" : "#ecf0f1"}
                                                color={star <= Math.round(player.rating || 0) ? "#f1c40f" : "#bdc3c7"}
                                                className="star-icon"
                                                onClick={() => handleRate(player.id, star)}
                                            />
                                        ))}
                                    </div>
                                    <span className="vote-count">({player.votes || 0} Oy)</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {sorted.length === 0 && (
                    <p style={{ color: '#95a5a6', marginTop: 40 }}>
                        Bu filtrelere uygun oyuncu bulunamadı.
                    </p>
                )}
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-box">
                        <button className="close-btn" onClick={() => setShowModal(false)}>
                            <X size={24} />
                        </button>

                        <h2>{editingId ? 'Profili Düzenle' : 'Saha Yıldızı Ol'}</h2>
                        <p>Ad, soyad ve en az bir iletişim bilgisi yeterli.</p>

                        <form onSubmit={handleSave}>
                            <div className="form-row-split">
                                <div className="form-group">
                                    <label>Ad</label>
                                    <input name="name" value={form.name} onChange={handleChange} required />
                                </div>
                                <div className="form-group">
                                    <label>Soyad</label>
                                    <input name="surname" value={form.surname} onChange={handleChange} required />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Mevki</label>
                                <select name="position" value={form.position} onChange={handleChange}>
                                    {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>

                            <div className="form-row-split">
                                <div className="form-group">
                                    <label>Şehir</label>
                                    <select
                                        name="city"
                                        value={form.city}
                                        onChange={(e) => setForm({ ...form, city: e.target.value, district: "" })}
                                        required
                                    >
                                        <option value="">Seçiniz</option>
                                        {Object.keys(TURKEY_CITIES).map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>İlçe</label>
                                    <select
                                        name="district"
                                        value={form.district}
                                        onChange={handleChange}
                                        disabled={!form.city}
                                    >
                                        <option value="">Seçiniz</option>
                                        {form.city && TURKEY_CITIES[form.city]?.map((d) => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Telefon</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    placeholder="05XX XXX XX XX"
                                />
                            </div>

                            <div className="form-group">
                                <label>E-Posta</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="mail@ornek.com"
                                />
                            </div>

                            <label className="pm-consent">
                                <input
                                    type="checkbox"
                                    checked={form.consent}
                                    onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                                />
                                <span>
                                    İletişim bilgilerimin (telefon / e-posta) diğer kullanıcılara <b>açık şekilde
                                    gösterilmesini</b> ve bu bilgiler üzerinden iletişime geçilmesini kabul ediyorum.
                                    Paylaştığım bilgilerin doğruluğundan ben sorumluyum; SahaLive yalnızca aracı
                                    platformdur ve kullanıcılar arasındaki iletişim, buluşma veya anlaşmalardan
                                    doğabilecek sonuçlardan sorumlu tutulamaz.
                                </span>
                            </label>

                            <button type="submit" className="save-modal-btn">
                                {editingId ? 'Güncelle' : 'Yayınla'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlayerMarket;
